import { supabase } from '@/integrations/supabase/client';

export interface EnhancementOperation {
  id: string;
  operation_type: 'bulk_enhance' | 'single_enhance' | 'quiz_generate' | 'practice_generate';
  started_at: string;
  completed_at?: string;
  performed_by?: string;
  total_items: number;
  processed_items: number;
  successful_items: number;
  failed_items: number;
  status: 'in_progress' | 'completed' | 'failed' | 'cancelled';
  error_message?: string;
  metadata?: any;
}

export interface EnhancementLog {
  id: string;
  operation_id: string;
  module_id: string;
  course_id: string;
  enhancement_type: 'content' | 'quiz' | 'practice' | 'audio';
  started_at: string;
  completed_at?: string;
  status: 'processing' | 'success' | 'failed';
  original_word_count?: number;
  enhanced_word_count?: number;
  code_blocks_added?: number;
  ai_model_used?: string;
  processing_time_ms?: number;
  error_message?: string;
  metadata?: any;
}

export const contentEnhancementTracker = {
  // Create a new enhancement operation
  async createOperation(
    operationType: EnhancementOperation['operation_type'],
    totalItems: number,
    metadata?: any
  ): Promise<string | null> {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('content_enhancement_operations')
      .insert({
        operation_type: operationType,
        total_items: totalItems,
        performed_by: user?.id,
        metadata
      })
      .select('id')
      .single();

    if (error) {
      console.error('Failed to create operation:', error);
      return null;
    }

    return data.id;
  },

  // Create enhancement log for a module
  async createLog(
    operationId: string,
    moduleId: string,
    courseId: string,
    enhancementType: EnhancementLog['enhancement_type'],
    originalWordCount?: number
  ): Promise<string | null> {
    const { data, error } = await supabase
      .from('module_enhancement_logs')
      .insert({
        operation_id: operationId,
        module_id: moduleId,
        course_id: courseId,
        enhancement_type: enhancementType,
        original_word_count: originalWordCount
      })
      .select('id')
      .single();

    if (error) {
      console.error('Failed to create log:', error);
      return null;
    }

    return data.id;
  },

  // Update operation progress
  async updateOperation(
    operationId: string,
    updates: Partial<EnhancementOperation>
  ): Promise<boolean> {
    const { error } = await supabase
      .from('content_enhancement_operations')
      .update(updates)
      .eq('id', operationId);

    if (error) {
      console.error('Failed to update operation:', error);
      return false;
    }

    return true;
  },

  // Update log status
  async updateLog(
    logId: string,
    updates: Partial<EnhancementLog>
  ): Promise<boolean> {
    const { error } = await supabase
      .from('module_enhancement_logs')
      .update(updates)
      .eq('id', logId);

    if (error) {
      console.error('Failed to update log:', error);
      return false;
    }

    return true;
  },

  // Get recent operations
  async getRecentOperations(limit = 10): Promise<EnhancementOperation[]> {
    const { data, error } = await supabase
      .from('content_enhancement_operations')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Failed to fetch operations:', error);
      return [];
    }

    return (data || []) as EnhancementOperation[];
  },

  // Get logs for an operation
  async getOperationLogs(operationId: string): Promise<EnhancementLog[]> {
    const { data, error } = await supabase
      .from('module_enhancement_logs')
      .select('*')
      .eq('operation_id', operationId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Failed to fetch logs:', error);
      return [];
    }

    return (data || []) as EnhancementLog[];
  },

  // Get active operations
  async getActiveOperations(): Promise<EnhancementOperation[]> {
    const { data, error } = await supabase
      .from('content_enhancement_operations')
      .select('*')
      .eq('status', 'in_progress')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Failed to fetch active operations:', error);
      return [];
    }

    return (data || []) as EnhancementOperation[];
  },

  // Subscribe to operation updates
  subscribeToOperation(operationId: string, callback: (operation: EnhancementOperation) => void) {
    const channel = supabase
      .channel(`operation-${operationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'content_enhancement_operations',
          filter: `id=eq.${operationId}`
        },
        (payload) => {
          callback(payload.new as EnhancementOperation);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  // Subscribe to all operations
  subscribeToOperations(callback: (operation: EnhancementOperation) => void) {
    const channel = supabase
      .channel('all-operations')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'content_enhancement_operations'
        },
        (payload) => {
          callback(payload.new as EnhancementOperation);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }
};
